import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  validateCycleLength, MIN_CYCLE_BARS, maxCycleFor, CYCLE_UNITS, totalCalendarDays,
} from '../utils/fixedCycles';

/**
 * Asks for a cycle length, then hands control back so the next chart click sets the anchor.
 *
 * Validation runs on every keystroke rather than only on Apply: the bounds depend on how many bars
 * the chart holds, which the customer cannot be expected to know, so the limit has to be visible
 * while typing rather than revealed by a rejection afterwards.
 */
const FixedCycleDialog = ({ open, onClose, onApply, totalBars, initialValue, initialUnit, dates }) => {
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState(CYCLE_UNITS.BARS);

  const isDays = unit === CYCLE_UNITS.DAYS;
  const totalDays = totalCalendarDays(dates);
  const max = isDays ? maxCycleFor(totalDays) : maxCycleFor(totalBars);

  // Reset each time it opens, seeded with the previous cycle when re-editing one.
  useEffect(() => {
    if (open) {
      setValue(initialValue ? String(initialValue) : '');
      setUnit(initialUnit || CYCLE_UNITS.BARS);
    }
  }, [open, initialValue, initialUnit]);

  const result = validateCycleLength(value, totalBars, { unit, totalDays });
  // An untouched box is not an error yet — it is just empty. Showing "enter a cycle length" before
  // anyone has typed reads as a complaint about something they have not had a chance to do.
  const showError = value !== '' && !result.valid;

  const apply = () => {
    if (result.valid) onApply(result.value, unit);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Fixed Cycle Discovery</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter a cycle length. After applying, click any bar on the chart to anchor the cycle;
          lines are drawn every N {isDays ? 'calendar days' : 'bars'} in both directions.
        </Typography>
        {/* The unit changes the limits, so it sits ABOVE the input: choosing it afterwards would
            silently invalidate a value that was legal when it was typed. */}
        <ToggleButtonGroup
          exclusive
          size="small"
          value={unit}
          onChange={(e, v) => { if (v) setUnit(v); }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value={CYCLE_UNITS.BARS}>Bars</ToggleButton>
          <ToggleButton value={CYCLE_UNITS.DAYS}>Days</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={isDays ? 'Cycle length (calendar days)' : 'Cycle length (bars)'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && result.valid) apply(); }}
          error={showError}
          helperText={showError
            ? result.error
            : `Between ${MIN_CYCLE_BARS} and ${max} ${isDays ? 'calendar days' : 'bars'} for this chart.`}
          inputProps={{ inputMode: 'numeric' }}
        />
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {totalBars} bars loaded{totalDays ? `, spanning ${totalDays} calendar days` : ''}.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {/* Disabled rather than allowed-then-rejected: the rule is already on screen, so the button
            being inert is self-explanatory where an error after clicking would not be. */}
        <Button onClick={apply} variant="contained" disabled={!result.valid}>Apply</Button>
      </DialogActions>
    </Dialog>
  );
};

FixedCycleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  totalBars: PropTypes.number,
  initialValue: PropTypes.number,
  initialUnit: PropTypes.string,
  /** Chart dates, used to work out the calendar span the day limit is derived from. */
  dates: PropTypes.array,
};

export default FixedCycleDialog;
